/**
 * Local (PWA) test drive: upload a CSV, find duplicates, entirely on this device.
 *
 * ── Where this sits, and the spec gap it fills ────────────────────────────────────────────
 *
 * F15's ui-spec specifies IA 35–42, and **every data-entry path among them assumes F01's OAuth
 * connect flow** (screen 38 is explicitly "within the connect flow"). A visitor test-driving the
 * product has no account and no reason to grant a new vendor CRM access on the first click, so
 * CSV is the only ingest that asks nothing of them. F01 already scopes "CSV/XLSX upload as a
 * pseudo-source"; what was missing was its *local* screen. Recorded as
 * [B-127](../../../../docs/platform/TODO.md) rather than added silently.
 *
 * This screen composes parts of three specified surfaces rather than inventing a fourth:
 * **IA 37**'s capability gate, **IA 39**'s run progress, and the review-side reading of a
 * cluster. It is deliberately one screen: upload → progress → result is one continuous act for
 * a visitor, and routing between three pages for it would lose them.
 *
 * ⚠️ **The claim this screen makes must stay true: nothing is uploaded.** The file is read with
 * `File.text()`, matched in a Web Worker, and never sent anywhere — `apps/web/src/local/` has no
 * network call in it. That is the whole trust proposition of local mode, and it is a property of
 * the code rather than a promise in copy.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DefinitionList,
  EmptyState,
  HelpText,
  ProgressBar,
} from '@dedupr/ui';
import type { CandidatePair, Entity, RecordKind } from '@dedupr/match-core';
import { useLocalMatch } from '../local/useLocalMatch.js';
import type { CanonicalField, MappingProposal } from '@dedupr/connectors';
import { InstallOffer } from '../InstallOffer.js';
import { profileDataset, type DatasetProfile } from '@dedupr/profile';
import { openLocalStore } from '../local/store.js';
import { downloadCsv } from '../local/exportCsv.js';
import { buildSourceExport, isRefusal } from '../local/exportSource.js';
import type { ParsedCsv } from '../local/csv.js';
import { explainCapacity, type Capacity } from '../local/capacity.js';
import { GraduationPrompt } from '../GraduationPrompt.js';
import './LocalRun.css';

const TIER_TONE = { auto_merge: 'success', review: 'warning', ignore: 'neutral' } as const;
const TIER_LABEL = {
  auto_merge: 'Very likely the same',
  review: 'Needs a human',
  ignore: 'Probably different',
} as const;

/** One cluster, with the pairs that linked it. */
function ClusterCard({
  entity,
  pairs,
  kind,
}: {
  entity: Entity;
  pairs: CandidatePair[];
  /* ⚠️ `RecordKind`, not a two-value union: ADR-029 added a third (`user`, reference data). Reference
     data is never matched, so a `user` cluster cannot exist — but typing this narrowly meant the
     screen could not accept the kind a mapping now legitimately reports, and widening it here is
     honest where a cast would have hidden the question. The label falls back to "record", which is
     true of anything and claims nothing. */
  kind: RecordKind;
}) {
  const linking = pairs.filter(
    (p) => entity.members.includes(p.record_a_id) && entity.members.includes(p.record_b_id)
  );
  // The weakest link is what a reviewer should judge the cluster by: a 25-member cluster held
  // together by one 0.71 pair is far less certain than its strongest pair suggests.
  const weakest = linking.reduce<CandidatePair | null>(
    (min, p) => (min === null || p.score < min.score ? p : min),
    null
  );

  return (
    <Card
      /* V-19b: found in a browser — a companies file rendered "look like one person" about two
         companies. The kind comes from the confirmed mapping's object, same as the records'. */
      title={`${entity.members.length} records look like one ${
        kind === 'company' ? 'company' : kind === 'person' ? 'person' : 'record'
      }`}
      headingLevel={3}
      tone={entity.oversized ? 'warning' : 'default'}
      action={
        weakest ? (
          <Badge variant={TIER_TONE[weakest.tier]}>{TIER_LABEL[weakest.tier]}</Badge>
        ) : null
      }
    >
      {entity.oversized ? (
        <Callout tone="warning" title="This group is unusually large">
          Groups this big are usually one wrong link joining two unrelated sets of records, so
          nothing here is treated as automatic. Look for the weakest pair below.
        </Callout>
      ) : null}

      <DefinitionList
        items={[
          { term: 'Rows', value: entity.members.join(', ') },
          {
            term: 'Links',
            value: `${entity.pair_count} pair${entity.pair_count === 1 ? '' : 's'}`,
            hint: 'Each link is two records the engine compared and scored.',
          },
          ...(weakest
            ? [
                {
                  term: 'Weakest link',
                  // Rounded to a percentage: the raw float implies a precision the priors do not
                  // have, and a reviewer decides on "how sure", not on four decimal places.
                  value: `${Math.round(weakest.score * 100)}% — matched on ${weakest.blocking_key.replace(/_/g, ' ')}`,
                  hint: 'The least certain pair holding this group together.',
                },
              ]
            : []),
        ]}
      />

      {/* Structured evidence only, per ADR-038 — which fields agreed, never their values. */}
      {weakest ? (
        <ul className="web-local__evidence">
          {weakest.evidence.fields
            .filter((f) => f.level !== 'null')
            .map((f) => (
              <li key={f.field}>
                <span className="web-local__field">{f.field.replace(/_/g, ' ')}</span>
                <span className="web-local__level">{f.level}</span>
              </li>
            ))}
        </ul>
      ) : null}
    </Card>
  );
}

/**
 * Fields the engine can actually match on. The rest (`owner`, `created_at`, …) are carried for
 * context and for the relay's pull path, but a mapping is only *usable* on the strength of these —
 * so the confirmation screen leads with them.
 */
const MATCHABLE: CanonicalField[] = ['email', 'phone', 'full_name', 'first_name', 'last_name'];

/**
 * Every field this screen actually applies — `MATCHABLE` plus the two that add context.
 *
 * ⚠️ **The "not used" list is derived from this, not from `proposal.unmapped`.** The registry maps
 * `created_at` and `updated_at` too, and rightly — the relay's incremental pull filters on
 * `updated_at`. But `toRecords` never reads them, so they were claimed by the mapping (and therefore
 * absent from `unmapped`) while being used by nothing: a visitor saw `CreatedDate` in neither the
 * table nor the ignored list, and "those columns are ignored" implied everything else was not.
 * Found in a browser; no test could see it, because both lists were individually correct.
 */
const APPLIED: CanonicalField[] = [...MATCHABLE, 'company', 'domain', 'external_id'];

const FIELD_LABEL: Record<CanonicalField, string> = {
  email: 'Email',
  phone: 'Phone',
  full_name: 'Full name',
  first_name: 'First name',
  last_name: 'Last name',
  company: 'Company',
  domain: 'Domain',
  external_id: 'Record ID',
  owner: 'Owner',
  created_at: 'Created',
  updated_at: 'Last modified',
};

/**
 * Whether this device has room to keep the run — [B-114](../../../../docs/platform/TODO.md).
 *
 * ⚠️ **`store.estimate()` existed and nothing called it.** The local run persists to IndexedDB so a
 * refresh does not lose it, and a browser refuses that write with a `QuotaExceededError` once the
 * origin's allowance is gone — which would have surfaced *after* a run someone had waited for, as a
 * failure with no explanation and nothing they could do about it.
 *
 * Renders **nothing** in the ordinary case, and that is deliberate: a storage figure on every visit
 * is noise that trains people to ignore the one time it matters. It also stays silent when the
 * browser declines to estimate — Firefox and Safari often do — because an absent figure is not a
 * problem, and inventing a warning from a missing number is exactly the false alarm this avoids.
 */
function StorageNotice({ rows }: { rows: number }) {
  const [tight, setTight] = useState<{ freeMb: number; needMb: number } | null>(null);

  useEffect(() => {
    let live = true;
    void openLocalStore()
      .then((store) => store.estimate())
      .then(({ usage, quota }) => {
        if (!live || usage === null || quota === null || quota === 0) return;
        /* ~1KB per record, from the stored shape (four short fields plus ids) with generous room
           for IndexedDB's own overhead. Deliberately pessimistic: a warning that never fires is
           worth less than one that occasionally fires early. */
        const needMb = Math.ceil((rows * 1024) / 1_000_000);
        const freeMb = Math.floor((quota - usage) / 1_000_000);
        if (freeMb < needMb * 2) setTight({ freeMb, needMb });
      })
      .catch(() => {
        /* An unreadable estimate is not a problem to report. The write itself still fails loudly if
           it fails, which is the guarantee that matters. */
      });
    return () => {
      live = false;
    };
  }, [rows]);

  if (!tight) return null;

  return (
    <Callout tone="warning" title="This device may be low on space">
      Your browser reports about {tight.freeMb} MB free for this site, and keeping{' '}
      {rows.toLocaleString()} records needs roughly {tight.needMb} MB. The run will still work — but
      if the browser runs out, the result cannot be saved and a refresh would lose it. Clearing space
      or using a smaller file avoids that.
    </Callout>
  );
}

/**
 * What the profiler found, per column — M3, rendered where the question is actually being asked.
 *
 * ⚠️ **It sits on the mapping screen deliberately.** "Is this column any good?" is the question a
 * person is answering when they confirm a mapping, and the answer is a measurement they do not have:
 * a column can be 100% populated and still be useless (900 rows saying `info@acme.com`), which is
 * exactly the case completeness alone calls excellent. Putting the profile anywhere else means
 * showing it after the decision it exists to inform.
 *
 * **Reports, never grades.** Each column gets its numbers and, where a format was inferred, real
 * non-conforming values. A stranger's export is full of legitimate things we did not anticipate —
 * `x2481` is a perfectly good extension — so the screen shows what is there and lets the person who
 * knows their data draw the conclusion.
 */
function ColumnProfile({ profile }: { profile: DatasetProfile }) {
  const pct = (n: number) => `${Math.round(n * 100)}%`;

  return (
    <details className="web-local__profile">
      {/* Collapsed by default: the mapping is the task, and this is the evidence behind it. Open by
          default it would push the actual controls below the fold on a laptop. */}
      <summary className="web-local__profilesummary">
        What is in each column ({profile.fields.length} columns, {profile.rows.toLocaleString()} rows)
      </summary>

      {profile.identityCandidates.length > 0 ? (
        <HelpText>
          Best suited to identifying a person, strongest first:{' '}
          <strong>{profile.identityCandidates.slice(0, 3).join(', ')}</strong>. That is a suggestion
          from the data in this file, not a rule — you know your own columns.
        </HelpText>
      ) : null}

      <table className="web-local__profiletable">
        <thead>
          <tr>
            <th scope="col">Column</th>
            <th scope="col">Filled in</th>
            <th scope="col">Different values</th>
            <th scope="col">Holds</th>
          </tr>
        </thead>
        <tbody>
          {profile.fields.map((f) => (
            <tr key={f.field}>
              <th scope="row">{f.field}</th>
              <td>{pct(f.completeness)}</td>
              <td>
                {pct(f.distinctness)}
                {/* The single most useful thing on this table: a dominant value is why an apparently
                    complete column is a poor key, and naming it is what makes that actionable. */}
                {f.topValues[0] && f.distinctness < 0.5 ? (
                  <span className="web-local__profilenote">
                    {' '}
                    — “{f.topValues[0].value}” {f.topValues[0].count}×
                  </span>
                ) : null}
              </td>
              <td>
                {f.format ? f.format.kind : 'text'}
                {f.pii === 'direct' ? (
                  <span className="web-local__profilepii"> personal</span>
                ) : null}
                {f.format && f.format.conformity < 0.9 ? (
                  <span className="web-local__profilenote">
                    {' '}
                    — {pct(1 - f.format.conformity)} unlike the rest
                    {f.format.examples[0] ? ` (e.g. “${f.format.examples[0]}”)` : ''}
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <HelpText>
        Figures describe this file only. “Personal” is a guess from the column name and its values —
        worth correcting by eye, and never the only thing a privacy decision rests on.
      </HelpText>
    </details>
  );
}

/**
 * Confirm the mapping before anything runs.
 *
 * ⚠️ **This screen exists because a mapping is a guess about someone else's data.** Reading the
 * wrong column as `email` produces a confidently wrong duplicate count — a specific, plausible-
 * looking number that is simply not true — which is the exact failure the product exists to
 * prevent. So the recognition, its evidence, and every resolved column are all shown, and every one
 * of them can be changed.
 *
 * It also shows the **first row's value** per field. A header can be right and the column still
 * wrong (two columns called `Email`, a shifted export), and a real value is the fastest way for
 * someone who knows their own data to see it.
 */
function MappingConfirmation({
  mapping,
  headers,
  rowCount,
  sampleRow,
  profile,
  truncated,
  capacity,
  onChange,
  onConfirm,
  onCancel,
  onForgetTemplate,
}: {
  mapping: MappingProposal;
  headers: string[];
  rowCount: number;
  sampleRow: string[];
  profile: DatasetProfile;
  truncated: { kept: number; total: number } | null;
  capacity: Capacity | null;
  onChange: (field: CanonicalField, column: number | null) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Discard the saved mapping for this shape of file (FR-4). Absent when there is none. */
  onForgetTemplate?: () => void;
}) {
  const { fingerprint } = mapping;
  /* IA 42's prompt, opened from the truncation notice below. Local state because the ceiling is this
     component's own fact — nothing above it needs to know the modal is open. */
  const [graduation, setGraduation] = useState(false);
  /* Whether any column came from a mapping this client confirmed before. `via: 'saved'` is set only by
     `applyTemplate`, so this is a fact about provenance rather than a guess. */
  const fromTemplate = mapping.fields.some((f) => f.via === 'saved');

  /* Every header not claimed by a field this screen applies. Computed here rather than taken from
     `mapping.unmapped`, for the reason on `APPLIED`. */
  const usedColumns = new Set(
    mapping.fields.filter((f) => APPLIED.includes(f.field) && f.column !== null).map((f) => f.column)
  );
  const ignoredColumns = headers.filter((_, i) => !usedColumns.has(i));

  return (
    <Card title="Check the columns before we start" headingLevel={2}>
      {fingerprint.fallback ? (
        <Callout tone="info" title="We did not recognise which system this came from">
          That is fine — columns were matched by their names instead. Check them below, because if a
          guess is wrong the result will be wrong in the same way.
        </Callout>
      ) : (
        <Callout
          tone="success"
          title={`This looks like a ${fingerprint.provider.displayName} export`}
        >
          {/* The evidence, not just the verdict. A claim about someone's own data should be
              checkable by them — and if it is wrong, the named column is what tells them so. */}
          {/* ⚠️ Rendered as real elements, not by joining with a markup string — React escapes a
              string, so `join('</code>, <code>')` would print the tags as visible text. */}
          Recognised from{' '}
          {fingerprint.matchedOn.length > 0
            ? fingerprint.matchedOn.map((h, i) => (
                <span key={h}>
                  {i > 0 ? ', ' : ''}
                  <code>{h}</code>
                </span>
              ))
            : 'your choice'}
          , so columns were matched using {fingerprint.provider.displayName}&apos;s own field names.
        </Callout>
      )}

      {/* ⚠️ **"You mapped a file like this before" is a DIFFERENT claim from "we recognised this", and
          it is the stronger one** — so it is stated separately and above, rather than folded into the
          recognition callout. A person who knows the mapping is theirs checks it differently from one
          being shown a guess. It also offers to discard it: a saved mapping that has become wrong must
          be removable, not worked around by correcting it every time. */}
      {fromTemplate ? (
        <Callout tone="info" title="Using the columns you confirmed last time">
          A file with these same headers was mapped before, so that mapping is filled in below rather
          than guessed at. Every field is still yours to change.
          {onForgetTemplate ? (
            <p className="web-local__forget">
              <Button variant="ghost" size="sm" onClick={onForgetTemplate}>
                Forget the saved mapping and start from a fresh guess
              </Button>
            </p>
          ) : null}
        </Callout>
      ) : null}

      {truncated ? (
        /* ⚠️ **The estimate AND its basis, never a bare verdict** — F15's rule, and the reason this
           says what the limit is *made of* rather than just quoting a number. A visitor told "too
           big" learns nothing; one told "this build scores in TypeScript, so 50,000 is what finishes
           in seconds rather than minutes" can judge whether the first 50,000 answers their question.
           ⚠️ It also does NOT claim the published ~1–2M figure: that assumes DuckDB-WASM over
           columnar data, which this build does not use, and it remains an unmeasured assumption
           (A-3) either way. Quoting it here would be borrowing confidence from a number nobody has
           checked. */
        <Callout tone="warning" title={`Reading the first ${truncated.kept.toLocaleString()} of ${truncated.total.toLocaleString()} rows`}>
          <p>
            This build matches in your browser, in plain JavaScript over records held in memory — so
            it is sized for a run that <strong>finishes in seconds rather than one that hangs the
            tab</strong>. {truncated.kept.toLocaleString()} rows is where that holds.
          </p>
          {/* ⚠️ The DERIVED basis (A-3, step 3). The limit is no longer a constant: it is this
              device's reported memory divided by the bytes a row of THIS width costs. Saying so is
              what IA 38 means by "the estimate and its basis" — a figure someone can argue with
              rather than merely believe, which is F06's savings-estimate precedent. */}
          {capacity ? <p>{explainCapacity(capacity)}</p> : null}
          <p>
            The result below describes those rows only, not the whole file. To check the rest, split
            the export or narrow it before you upload — and tell us, because a real file that needs
            more is exactly what decides how much further this goes.
          </p>
          {/* ⚠️ **IA 42 / ADR-041: a ceiling is a quantified next step, never a bare limitation.**
              This callout already explained the limit honestly and stopped there — which is the
              *"buyer who fought for local-only approval and then hits a wall"* the ADR describes. The
              control opens the graduation prompt with the real number of rows going uncompared.
              ⚠️ A button rather than an auto-opening modal: the ceiling is not an emergency, the run
              below still works, and interrupting a result somebody is reading to sell them something
              is precisely how this becomes the nag [08 §4](../../../docs/platform/08-information-architecture.md)
              warns against. */}
          <Button variant="secondary" onClick={() => setGraduation(true)}>
            What would checking all {truncated.total.toLocaleString()} take?
          </Button>
        </Callout>
      ) : null}

      {graduation && truncated ? (
        <GraduationPrompt
          ceiling="This file is larger than one device can compare"
          /* ⚠️ The QUANTITY ADR-041 requires, computed rather than phrased vaguely: the rows that are
             not being compared at all, which is the value actually being missed. */
          missing={`${(truncated.total - truncated.kept).toLocaleString()} of this file's ${truncated.total.toLocaleString()} records are not being compared to anything — so any duplicate that exists only among them is invisible in the result below.`}
          because="Matching holds every record in this browser tab's memory at once, so the ceiling is this device's memory rather than a licence limit. Cloud processing has no such bound."
          onDismiss={() => setGraduation(false)}
        />
      ) : null}

      <StorageNotice rows={rowCount} />

      <ColumnProfile profile={profile} />

      <div className="web-local__maptable" role="group" aria-label="Column mapping">
        {APPLIED.map((field) => {
          const entry = mapping.fields.find((f) => f.field === field)!;
          const sample = entry.column === null ? null : sampleRow[entry.column];
          return (
            <div key={field} className="web-local__maprow">
              <label className="web-local__maplabel" htmlFor={`map-${field}`}>
                {FIELD_LABEL[field]}
                {MATCHABLE.includes(field) ? null : <span className="web-local__mapopt"> (optional)</span>}
              </label>
              <select
                id={`map-${field}`}
                className="web-local__mapselect"
                value={entry.column === null ? '' : String(entry.column)}
                onChange={(e) => onChange(field, e.currentTarget.value === '' ? null : Number(e.currentTarget.value))}
              >
                <option value="">— not in this file —</option>
                {headers.map((h, i) => (
                  <option key={i} value={i}>
                    {h}
                  </option>
                ))}
              </select>
              {/* A real value from the file. `—` when unmapped, never an empty cell that reads as
                  a blank value rather than as no column. */}
              <span className="web-local__mapsample">{sample && sample.trim() !== '' ? sample : '—'}</span>
            </div>
          );
        })}
      </div>

      {ignoredColumns.length > 0 ? (
        <HelpText>
          Not used: {ignoredColumns.join(', ')}. Those columns are ignored — pick one above if it
          should be used.
        </HelpText>
      ) : null}

      {mapping.usable ? (
        <HelpText>
          {rowCount.toLocaleString()} rows will be compared on this device. Nothing is uploaded.
        </HelpText>
      ) : (
        /* Refusing, and saying why in terms of the consequence rather than the rule. Running anyway
           would report "no duplicates", which reads as "your data is clean". */
        <Callout tone="warning" title="Nothing here can identify a person yet">
          At least one of email, phone, or a name is needed. Without one, every row would be
          unmatchable and the result would look like &ldquo;no duplicates&rdquo; rather than
          &ldquo;we could not tell&rdquo;.
        </Callout>
      )}

      <div className="web-local__mapactions">
        <Button onClick={onConfirm} disabled={!mapping.usable}>
          Find duplicates
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Choose another file
        </Button>
      </div>
    </Card>
  );
}

/**
 * Download the cleaned file.
 *
 * ⚠️ **This is the end of the journey, and until it existed there wasn't one.** Upload → map →
 * profile → match → review → *nothing*: a visitor could confirm every duplicate in their file and
 * leave with a screen. Reviewing duplicates is not the job; getting a usable file back is.
 *
 * It exports whatever has actually been **applied** — a cluster the engine found but nobody merged
 * exports as its separate records, because shipping a speculative merge would be delivering a
 * decision no human made (M8's business rule 2, and the same reasoning as the hosted export).
 */
function ExportButton({
  sourceId,
  saved,
}: {
  /** The persisted source, once the Z-0 save succeeded. */
  sourceId: string | null;
  saved: 'pending' | 'saved' | 'failed' | null;
}) {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  /* ⚠️ Rebuilt from the STORE, not from this tab's parse. The in-memory version keyed its row
     lookup by the file's raw id while the stored records carry Z-0's namespaced ids — every lookup
     missed, so a merged row would have exported with empty original columns. `buildSourceExport`
     derives the ids with the same function the ingest used, and it is the identical code path the
     Destinations screen uses, so this button cannot silently diverge from it. */
  const run = async () => {
    if (!sourceId) return;
    setBusy(true);
    try {
      const store = await openLocalStore();
      try {
        const built = await buildSourceExport(store, sourceId);
        if (isRefusal(built)) {
          setSummary(built.detail);
          return;
        }
        downloadCsv(built.filename, built.csv);
        // The history row is written only after the download was actually handed over.
        await store.recordExport(built.entry);
        /* Stated afterwards, with numbers: a download that silently produced a different row count
           from what the screen implied is the one surprise this feature can cause. */
        const counts = built.summary;
        setSummary(
          counts.merged === 0
            ? `${counts.rows} rows — no merges have been applied, so nothing was collapsed.`
            : `${counts.rows} rows: ${counts.merged} merged cluster(s) replaced ${counts.recordsRemoved + counts.merged} records.`
        );
      } finally {
        store.close();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button disabled={busy || !sourceId} onClick={() => void run()}>
        Download the cleaned file
      </Button>
      {saved === 'failed' ? (
        /* Not a dead button with no explanation: the save failed, so there is nothing on the device
           to rebuild the file from — and the person deserves to know those two facts are linked. */
        <HelpText>The download needs the saved run, and this run could not be saved.</HelpText>
      ) : null}
      {summary ? <HelpText>{summary}</HelpText> : null}
    </>
  );
}

export function LocalRun() {
  const { phase, progress, parsed, mapping, result, error, capabilityError, truncated, capacity, saved, savedSourceId, matchedWith, load, start, remap, reset, forgetTemplate } =
    useLocalMatch();

  /**
   * The profile, computed once per file rather than per render.
   *
   * ⚠️ Keyed on `parsed` itself, not on a row count: remapping a column re-renders this screen
   * constantly, and re-profiling several thousand rows on every keystroke of a `<select>` would make
   * the mapping controls stutter on exactly the file sizes this is for. The profile does not depend
   * on the mapping at all — it describes the columns as they are.
   */
  const profile = useMemo(
    () =>
      parsed
        ? profileDataset(
            /* Row arrays → objects keyed by header, because the profiler is source-agnostic and
               knows nothing about CSV. `headers` rather than the row's own length: a ragged row is
               reported separately, and letting it invent a column here would put a phantom field in
               the table. */
            parsed.rows.map((row) =>
              Object.fromEntries(parsed.headers.map((h, i) => [h, row[i] ?? null]))
            ),
            parsed.headers
          )
        : { rows: 0, fields: [], identityCandidates: [] },
    [parsed]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  /* IA 37: the capability gate names WHICH capability is missing. "Unsupported browser" tells a
     visitor nothing they can act on. */
  if (capabilityError) {
    return (
      <div className="web-local">
        {/* ⚠️ **No `PageHeader` here any more.** This is a card ON Sources now, not a route, and the
            shell already titles the page — a second `h1` is the duplicated-heading defect this repo's
            history records, and it is the exact thing moving a route into a screen reintroduces. */}
        <Callout tone="danger" title="This browser cannot run local mode">
          {capabilityError}
        </Callout>
        <HelpText>
          Local mode processes your data on your own device, which needs browser features this one
          does not provide. Nothing was uploaded and nothing was processed.
        </HelpText>
      </div>
    );
  }

  return (
    <div className="web-local">
      {/* ⚠️ **No heading element at all** — see the capability note above: this composes into Sources
          now, and Sources owns the page's one `h1`. A second one is the duplicated-heading defect this
          repo's history records, and moving a route into a screen is exactly what reintroduces it. The
          copy survives as the lede it always was.
          ⚠️ Says CSV **or Excel**. The picker has accepted `.xlsx` since B-134 landed and this copy did
          not, so the capability shipped unadvertised — someone with an `.xlsx` export reads "upload a
          CSV", converts it by hand, and never learns they did not have to. Found by reading the live
          site rather than by any test. */}
      <HelpText>
        Upload a CSV or Excel export and see what we find. Your file is read in this browser and never
        leaves your device.
      </HelpText>

      <InstallOffer>
        It then opens from your home screen or dock and works with no connection — your data is
        already on this device, so there is nothing to fetch.
      </InstallOffer>

      {phase === 'idle' || phase === 'error' ? (
        <Card title="Choose a file" headingLevel={2}>
          <input
            ref={inputRef}
            type="file"
            // `.txt` too: plenty of real exports arrive with that extension and refusing them
            // would be a mystery to the person who exported the file.
            accept=".csv,.tsv,.txt,.xlsx,text/csv,text/tab-separated-values,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="web-local__file"
            aria-label="CSV or Excel file to check for duplicates"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) void load(file);
            }}
          />
          <HelpText>
            A header row plus one row per contact. It needs at least one of email, phone, or a
            name — those are what a person can be identified by.
          </HelpText>
          {error ? (
            <Callout tone="danger" title="That file could not be used">
              {error}
            </Callout>
          ) : null}
        </Card>
      ) : null}

      {phase === 'parsing' ? (
        <Card title="Reading the file" headingLevel={2}>
          <ProgressBar indeterminate label="Reading the file" />
        </Card>
      ) : null}

      {phase === 'mapping' && mapping && parsed ? (
        <MappingConfirmation
          mapping={mapping}
          headers={parsed.headers}
          rowCount={parsed.rows.length}
          capacity={capacity}
          sampleRow={parsed.rows[0] ?? []}
          profile={profile}
          truncated={truncated}
          onChange={remap}
          onConfirm={start}
          onCancel={reset}
          onForgetTemplate={forgetTemplate}
        />
      ) : null}

      {/* IA 39's run progress. Determinate, because the pair count is known once blocking is
          done — an indeterminate bar on a job that may run for a minute tells a visitor nothing
          about whether to wait. */}
      {phase === 'matching' ? (
        <Card title="Comparing records" headingLevel={2}>
          <ProgressBar
            value={Math.round(progress * 100)}
            label={`Comparing records: ${Math.round(progress * 100)}%`}
          />
          <HelpText>
            Running on your device, in this tab. Nothing has been sent anywhere.
          </HelpText>
        </Card>
      ) : null}

      {phase === 'done' && result ? (
        <>
          <Card title="What we found" headingLevel={2}>
            <DefinitionList
              items={[
                /* The file's own rows — NOT `result.stats.records`, which counts the whole union
                   once earlier files join the run. "Rows read: 37" against a 10-row file reads as
                   a parsing bug. */
                { term: 'Rows read', value: String(parsed ? parsed.rows.length : result.stats.records) },
                ...(matchedWith && matchedWith > 0
                  ? [
                      {
                        term: 'Matched together with',
                        value: `${matchedWith.toLocaleString()} records already on this device`,
                        /* Said plainly, because the group count below includes them. A group that
                           quietly spans a file loaded last week would otherwise look wrong against
                           the file just chosen. */
                        hint: 'Every run matches across everything on this device, so groups can span files.',
                      },
                    ]
                  : []),
                {
                  term: 'Groups of duplicates',
                  value: String(result.entities.length),
                  hint:
                    mapping?.fingerprint.object.kind === 'company'
                      ? 'Records that look like the same company.'
                      : 'Records that look like the same person.',
                },
                {
                  term: 'Pairs compared',
                  value: String(result.stats.pairs),
                  hint: 'Only plausible pairs are compared — every row against every other row would be billions of comparisons.',
                },
                ...(result.stats.unblockable > 0
                  ? [
                      {
                        term: 'Rows we could not check',
                        value: String(result.stats.unblockable),
                        /* Stated, never folded into "no duplicates". "We found nothing" and "we
                           could not look" are different answers and only one means clean data. */
                        hint: 'These rows had no email, phone or name, so there was nothing to match them on. That is not the same as having no duplicates.',
                      },
                    ]
                  : []),
              ]}
            />
            {/* ⚠️ **Z-0.** The run is now written to this device, and the outcome is stated rather
                than assumed. A save the visitor believes happened and did not is the worst failure
                available here — they close the tab. */}
            {saved === 'failed' ? (
              <Callout tone="warning" title="This run was not saved to your device">
                The results above are correct, but they are only in this tab — reloading will lose
                them. Your browser may be out of space or blocking storage.
              </Callout>
            ) : saved === 'saved' ? (
              <HelpText>
                Saved to this device. It is on the Dashboard, Sources, Findings and Matches screens
                now, and it survives a reload.
              </HelpText>
            ) : null}
            {parsed && parsed.raggedRows > 0 ? (
              <Callout tone="warning" title={`${parsed.raggedRows} rows did not line up with the header`}>
                That usually means a value contains the separator without being quoted, which
                shifts every column after it — so those rows may have been read wrongly. The
                counts above may be off by that much.
              </Callout>
            ) : null}
            {mapping ? (
              <HelpText>
                Matched on:{' '}
                {mapping.fields
                  .filter((f) => f.column !== null && MATCHABLE.includes(f.field))
                  .map((f) => f.header)
                  .join(', ')}
                {mapping.fingerprint.fallback
                  ? '. Columns were matched by name — if that was wrong, the result is too.'
                  : `. Read as a ${mapping.fingerprint.provider.displayName} export.`}
              </HelpText>
            ) : null}
            <div className="web-local__result-actions">
              <ExportButton sourceId={savedSourceId} saved={saved} />
              <Button variant="secondary" onClick={reset}>
                Try another file
              </Button>
            </div>
          </Card>

          {result.entities.length === 0 ? (
            <EmptyState
              title="No duplicates found"
              description={
                result.stats.unblockable > 0
                  ? 'Nothing in the rows we could check looked like a duplicate. Note that some rows could not be checked at all — see above.'
                  : 'Nothing in this file looked like a duplicate of anything else in it.'
              }
            />
          ) : (
            <>
              {/* ⚠️ Says plainly that nothing was changed. This screen only ever reads a file;
                  merging is F04's executor behind snapshot → dry-run → approve → rollback, and a
                  visitor should not have to wonder whether we just edited their data.
                  ⚠️ Reworded for Z-0: "saved anywhere" became a lie the day the run started
                  persisting — the results ARE saved, to this device, and the message above says so.
                  What is still true, and what this callout is for: the FILE was not touched. */}
              <Callout tone="info" title="Your file has not been changed">
                The file on your computer was only read. No records were merged or edited — the
                results are kept in this browser, and nothing was uploaded.
              </Callout>
              <div className="web-local__clusters">
                {result.entities.map((entity) => (
                  <ClusterCard
                    key={entity.id}
                    entity={entity}
                    pairs={result.pairs}
                    kind={mapping?.fingerprint.object.kind ?? 'person'}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
